/**
 * Vue2 精确计时器工具
 * 基于 setTimeout 递归实现，自动校正时间差
 */
export default class AccurateTimer {
  /**
   * 创建计时器实例
   * @param {Function} callback - 定时执行的回调函数
   * @param {number} interval - 时间间隔(毫秒)
   * @param {Object} options - 配置选项
   * @param {boolean} options.immediate - 是否立即执行第一次
   * @param {Vue} options.vueInstance - Vue组件实例，用于自动在beforeDestroy时清理
   */
  constructor(
    callback,
    interval,
    { immediate = false, vueInstance = null } = {}
  ) {
    this.callback = callback;
    this.interval = interval;
    this.immediate = immediate;
    this.vueInstance = vueInstance;

    // 状态管理
    this.timerId = null;
    this.lastExecuteTime = null;
    this.isRunning = false;

    // 自动绑定Vue组件生命周期
    if (this.vueInstance) {
      this._bindVueLifecycle();
    }
  }

  /**
   * 绑定Vue组件生命周期，在组件销毁时自动停止计时器
   */
  _bindVueLifecycle() {
    const originalBeforeDestroy = this.vueInstance.beforeDestroy;
    this.vueInstance.beforeDestroy = function () {
      // 执行组件原有的beforeDestroy钩子
      if (typeof originalBeforeDestroy === "function") {
        originalBeforeDestroy.call(this);
      }
      // 停止计时器
      this.stop();
    }.bind(this);
  }

  /**
   * 启动计时器
   */
  start() {
    if (this.isRunning) return;

    this.isRunning = true;
    this.lastExecuteTime = Date.now();

    // 立即执行第一次
    if (this.immediate) {
      this.callback(0);
    }

    this._run();
  }

  /**
   * 停止计时器
   */
  stop() {
    if (!this.isRunning) return;

    this.isRunning = false;
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  /**
   * 重启计时器
   */
  restart() {
    this.stop();
    this.start();
  }

  /**
   * 递归执行的核心方法
   */
  _run() {
    if (!this.isRunning) return;

    // 计算下一次执行的预期时间
    const expectedNextTime = this.lastExecuteTime + this.interval;
    const now = Date.now();
    const delay = expectedNextTime - now;

    // 更新上一次执行时间（使用预期时间而非实际时间，减少累积误差）
    this.lastExecuteTime = expectedNextTime;

    // 安排下一次执行
    this.timerId = setTimeout(() => {
      // 计算实际执行与预期的时间差
      const actualTime = Date.now();
      const timeDiff = actualTime - expectedNextTime;

      // 执行回调，传递时间差
      this.callback(timeDiff);

      // 继续递归
      this._run();
    }, Math.max(0, delay)); // 确保延迟时间不为负
  }

  /**
   * 更新时间间隔
   * @param {number} newInterval - 新的时间间隔(毫秒)
   */
  updateInterval(newInterval) {
    this.interval = newInterval;
  }
}
